import React from "react";
import { Box, Tooltip as MuiTooltip, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { KpiFieldMeta } from "../Types/MLAnalyticsTypes";

interface KpiDeviationChartProps {
  kpiFields: KpiFieldMeta[];
  employeeValues: Record<string, number | null | undefined>;
  teamAverages: Record<string, number | null | undefined>;
}

/**
 * Whether a *higher* raw value is the better outcome for each field. Fields
 * not listed here (there shouldn't be any among KPI_RADAR_FIELDS) default to
 * "higher is better".
 */
const LOWER_IS_BETTER_FIELDS = new Set([
  "avg_late_marks_filled",
  "noncompliance_issue_count_filled",
  "total_leave_without_pay_filled",
]);

/** Below this absolute team-average value, a percentage deviation is too noisy/undefined to be meaningful. */
const MIN_MEANINGFUL_BASE = 0.1;
const MAX_DISPLAYED_PERCENT = 150;

interface DeviationDatum {
  field: string;
  label: string;
  employeeValue: number | null | undefined;
  teamAverage: number | null | undefined;
  percent: number | null;
  clipped: boolean;
}

const formatValue = (value: number | null | undefined): string =>
  value === null || value === undefined || Number.isNaN(value) ? "—" : value.toFixed(2);

/**
 * Renders the % value label anchored to the *inner* tip of its own bar
 * rectangle (never past its edges), so it can never overlap the YAxis
 * category labels even when a bar is clamped to the full domain width.
 * A white text-outline keeps it legible against both the colored bar and
 * the plain chart background for very short bars.
 */
const renderValueLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  if (value === null || value === undefined || Number.isNaN(value)) return null;

  const isPositive = value >= 0;
  const text = `${isPositive ? "+" : ""}${Math.round(value)}%`;
  const labelX = isPositive ? x + width - 4 : x + 4;
  const labelY = y + height / 2 + 4;

  return (
    <text
      x={labelX}
      y={labelY}
      textAnchor={isPositive ? "end" : "start"}
      fontSize={11}
      fontWeight={600}
      fill="#212121"
      stroke="#ffffff"
      strokeWidth={3}
      paintOrder="stroke"
    >
      {text}
    </text>
  );
};

/**
 * Diverging bar chart showing each KPI metric as a % deviation from the
 * manager's team average, rather than forcing every metric (ratings,
 * percentages, raw counts) onto one shared 0-100 radar axis. Positive
 * (green) always means "better than the team average" regardless of
 * whether the underlying metric is naturally higher-is-better or
 * lower-is-better (e.g. fewer late marks shows as a positive bar).
 */
const KpiDeviationChart: React.FC<KpiDeviationChartProps> = ({ kpiFields, employeeValues, teamAverages }) => {
  const data: DeviationDatum[] = kpiFields.map(({ field, label }) => {
    const employeeValue = employeeValues[field];
    const teamAverage = teamAverages[field];

    if (
      employeeValue === null ||
      employeeValue === undefined ||
      teamAverage === null ||
      teamAverage === undefined ||
      Number.isNaN(employeeValue) ||
      Number.isNaN(teamAverage)
    ) {
      return { field, label, employeeValue, teamAverage, percent: null, clipped: false };
    }

    const rawDiff = employeeValue - teamAverage;
    const effectiveDiff = LOWER_IS_BETTER_FIELDS.has(field) ? -rawDiff : rawDiff;
    const base = Math.abs(teamAverage);

    let percent: number;
    if (base < MIN_MEANINGFUL_BASE) {
      // Team average is ~0 (e.g. Leave Without Pay), so a ratio would blow up
      // or be undefined. Fall back to a coarse +/-100 "better/worse" signal.
      percent = effectiveDiff > 0 ? 100 : effectiveDiff < 0 ? -100 : 0;
    } else {
      percent = (effectiveDiff / base) * 100;
    }

    const clipped = Math.abs(percent) > MAX_DISPLAYED_PERCENT;
    const clampedPercent = Math.max(-MAX_DISPLAYED_PERCENT, Math.min(MAX_DISPLAYED_PERCENT, percent));

    return { field, label, employeeValue, teamAverage, percent: clampedPercent, clipped };
  });

  const hasAnyData = data.some((d) => d.percent !== null);

  if (!hasAnyData) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not enough team data to compute KPI comparisons.
      </Typography>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 1 }}>
        % deviation from team average per metric (green = better than the team, red = worse).
      </Typography>
      <Box sx={{ width: "100%", height: Math.max(280, data.length * 42) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 10, right: 40, left: 20, bottom: 10 }}
          >
            <XAxis
              type="number"
              domain={[-MAX_DISPLAYED_PERCENT, MAX_DISPLAYED_PERCENT]}
              tickFormatter={(value: number) => `${value}%`}
            />
            <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 12 }} />
            <ReferenceLine x={0} stroke="#9e9e9e" />
            <Tooltip
              formatter={(value: number, _name, item) => {
                const datum = item?.payload as DeviationDatum | undefined;
                if (!datum || datum.percent === null) return ["No data", ""];
                const sign = value >= 0 ? "+" : "";
                return [
                  `${sign}${value.toFixed(0)}%${datum.clipped ? " (capped)" : ""} — Employee: ${formatValue(
                    datum.employeeValue
                  )}, Team avg: ${formatValue(datum.teamAverage)}`,
                  "Deviation",
                ];
              }}
            />
            <Bar dataKey="percent" radius={[4, 4, 4, 4]} isAnimationActive={false}>
              {data.map((entry) => (
                <Cell
                  key={entry.field}
                  fill={
                    entry.percent === null
                      ? "#bdbdbd"
                      : entry.percent >= 0
                      ? "#2e7d32"
                      : "#d32f2f"
                  }
                />
              ))}
              <LabelList dataKey="percent" content={renderValueLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <MuiTooltip title="Percentages beyond ±150% are capped for readability; hover a bar to see exact values.">
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          sx={{ display: "block", mt: 0.5, cursor: "help" }}
        >
          Some metrics (e.g. Leave Without Pay) use a fixed ±100% indicator when the team average is ~0.
        </Typography>
      </MuiTooltip>
    </Box>
  );
};

export default KpiDeviationChart;
